import type { FC } from 'react';

import { StarIcon, StarStrokeIcon } from '../../icons';
import { styled } from '../../stitches.config';
import { Stack } from '../Stack/Stack';

const StarWrapper = styled('div', {
  position: 'relative',
  variants: {
    size: {
      small: {
        iconography: '$small',
      },
      medium: {
        iconography: '$medium',
      },
      large: {
        iconography: '$large',
      },
      extraLarge: {
        iconography: '$extraLarge',
      },
    },
  },
});

const StarBase = styled('svg', {
  position: 'absolute',
  top: 0,
  left: 0,
  color: '$gray300',
  variants: {
    size: {
      small: {
        iconography: '$small',
      },
      medium: {
        iconography: '$medium',
      },
      large: {
        iconography: '$large',
      },
      extraLarge: {
        iconography: '$extraLarge',
      },
    },
  },
});

const Star = styled('svg', {
  position: 'absolute',
  top: 0,
  left: 0,
  color: '$yellow500',
  overflow: 'hidden',
  clipPath: 'inset(0 50% 0 0)',
  variants: {
    size: {
      small: {
        iconography: '$small',
      },
      medium: {
        iconography: '$medium',
      },
      large: {
        iconography: '$large',
      },
      extraLarge: {
        iconography: '$extraLarge',
      },
    },
    fill: {
      empty: {
        clipPath: 'inset(0 100% 0 0)',
      },
      quarter: {
        clipPath: 'inset(0 56% 0 0)',
      },
      half: {
        clipPath: 'inset(0 50% 0 0)',
      },
      threeQuarter: {
        clipPath: 'inset(0 42% 0 0)',
      },
      full: {
        clipPath: 'inset(0 0 0 0)',
      },
    },
  },
});

type StarFill = 'full' | 'threeQuarter' | 'half' | 'quarter' | 'empty';

function getDetailedStarFills(score: number, totalStars: number): StarFill[] {
  const starFills: StarFill[] = [];
  const normalizedScore = (score / 10) * totalStars;

  for (let i = 0; i < totalStars; i++) {
    const diff = normalizedScore - i;

    if (diff >= 0.875) {
      starFills.push('full');
    } else if (diff >= 0.625) {
      starFills.push('threeQuarter');
    } else if (diff >= 0.375) {
      starFills.push('half');
    } else if (diff >= 0.125) {
      starFills.push('quarter');
    } else {
      starFills.push('empty');
    }
  }

  return starFills;
}

type StarSizes = 'small' | 'medium' | 'large' | 'extraLarge';

type RatingSummaryProps = {
  maxStars: number;
  score: number;
  size?: StarSizes;
};

export const RatingStar: FC<RatingSummaryProps> = ({ maxStars = 5, size = 'small', score }) => {
  const fills = getDetailedStarFills(score, maxStars);

  return (
    <Stack direction="row" gap="0">
      {fills.map((fill, i) => {
        return (
          <StarWrapper size={size} key={i}>
            <StarBase size={size}>
              <StarStrokeIcon color="feedbackSuccess" />
            </StarBase>
            <Star size={size} fill={fill}>
              <StarIcon color="feedbackSuccess" />
            </Star>
          </StarWrapper>
        );
      })}
    </Stack>
  );
};

export default RatingStar;
